import React from 'react';
import type { ICellRendererParams } from 'ag-grid-community';
import SourcePill from 'Components/atoms/SourcePill/SourcePill';
import { sourceMeta } from '../sourceMeta';

export type SourceCellRendererParams = ICellRendererParams & {
    /** How many pills to show before collapsing the rest into a "+N" chip. */
    maxPills?: number;
    icon?: React.ReactNode;
};

export type SourcePillsListProps = {
    value?: string | string[] | null;
    /** How many pills to show before collapsing the rest into a "+N" chip. */
    maxPills?: number;
    icon?: React.ReactNode;
};

/** Shared pill layout for grid cells and other inline tag/source displays. */
export const SourcePillsList: React.FC<SourcePillsListProps> = ({ value, maxPills = 2, icon }) => {
    const list: string[] = Array.isArray(value)
        ? value.filter((v): v is string => Boolean(v))
        : value
        ? [String(value)]
        : [];

    if (list.length === 0) {
        return <span className="text-text-low-emphasis">—</span>;
    }

    const shown = list.slice(0, maxPills);
    const extra = list.length - shown.length;

    return (
        <div className="inventory-source-cell">
            {shown.map((raw) => {
                const meta = sourceMeta(raw);
                return <SourcePill key={raw} label={meta.label} icon={icon || meta.icon} tone={meta.tone} title={raw} />;
            })}
            {extra > 0 && (
                <span className="rb-source-pill rb-source-pill--more" title={list.slice(maxPills).join(', ')}>
                    <span className="rb-source-pill__label">+{extra}</span>
                </span>
            )}
        </div>
    );
};

/**
 * Renders the source column as friendly pills. The column's `valueGetter`
 * supplies either a single source string or a string[] (software rows carry
 * several sources); everything past `maxPills` collapses into a "+N" chip.
 */
const SourceCellRenderer: React.FC<SourceCellRendererParams> = ({ value, maxPills = 3, icon }) => (
    <SourcePillsList value={value} maxPills={maxPills} icon={icon} />
);

export default SourceCellRenderer;
